import type { FilterConfig, FilterOption } from '../../../apis/graphql/types';
import { FilterType } from '../../../apis/graphql/types';

/**
 * True when a config is rendered by one of the multi-select controls
 * (SearchableMultiSelectControl or StaticMultiSelectControl). These controls
 * stage their value as `FilterOption[]` ({ value, label }).
 *
 * Committed shape depends on the filter: searchable multi-selects keep
 * `{ value, label }[]` in filter state (labels for chips and shareable links);
 * non-searchable enum lists commit plain `string[]`.
 *
 * Mirrors the discriminator order in FilterRow.renderControl: boolean radios and
 * Calendar/Range controls own their value shapes; everything else is multi-select.
 */
export function isMultiSelectConfig(config: FilterConfig): boolean {
  if (config.isBoolean) return false;
  if (config.isSearchable) return true;
  return config.type !== FilterType.Calendar && config.type !== FilterType.Range;
}

/**
 * Converts the control's staged `FilterOption[]` into the array of value strings
 * the query expects (e.g. [{ value: 'SPONSORSHIP_LINE_ITEM', label: 'Sponsorship' }]
 * → ['SPONSORSHIP_LINE_ITEM']).
 */
export function filterOptionsToValues(value: FilterOption[] | null | undefined): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((option) => option.value);
}

/**
 * Converts staged `FilterOption[]` into the shape stored in GlobalFilters on Apply.
 * Searchable filters preserve option objects; static enum lists flatten to value strings.
 */
export function commitMultiSelectValue(config: FilterConfig, value: FilterOption[]): FilterOption[] | string[] {
  if (config.isSearchable) return value;
  return filterOptionsToValues(value);
}

/**
 * Reverse-maps a committed array of value strings back into `FilterOption[]` for
 * display when the panel re-opens. Labels are restored from `config.staticOptions`
 * when available; dynamic/searchable sources fall back to the value as the label
 * until their options are (re)fetched.
 *
 * Defensive against legacy/shareable values that are already option objects.
 */
export function valuesToFilterOptions(config: FilterConfig, raw: unknown): FilterOption[] {
  if (!Array.isArray(raw)) return [];

  const labelByValue = new Map((config.staticOptions ?? []).map((option) => [option.value, option.label]));

  return raw.map((item) => {
    if (item && typeof item === 'object' && 'value' in item) {
      const option = item as { value: string; label?: string };
      return { value: option.value, label: option.label ?? labelByValue.get(option.value) ?? option.value };
    }
    const value = String(item);
    return { value, label: labelByValue.get(value) ?? value };
  });
}
